import { memo, useCallback, useRef } from "react";
import type { ReactNode } from "react";
import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { isFunction } from "lodash";
import { t } from "@/i18n-js/instance";
import type { CollisionArgs } from "@circle-react-shared/BuilderUI/Helpers/collisionDetection";
import { conditionalClosestCenter } from "@circle-react-shared/BuilderUI/Helpers/collisionDetection";
import { isAcceptableToDrop } from "@circle-react-shared/BuilderUI/Helpers/droppableAcceptsDraggable";
import {
  isBlockExport,
  isDroppableData,
  isSerializableBlockExport,
  isTemplateExport,
} from "@circle-react-shared/BuilderUI/Helpers/typePredicates";
import { useBuilderStore } from "@circle-react-shared/BuilderUI/Hooks/useBuilderStore";
import { useToast } from "@circle-react-uikit/ToastV2";
import type { SerializableBlockExport } from "../BuilderUI.types";

const removeNonSerializableProperties = (obj: Record<string, unknown>) => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (!isFunction(value)) {
      result[key] = value;
    }
  }
  return result;
};

const cloneAsSerializableBlockExport = (value: SerializableBlockExport) => {
  // Remove non-serializable functions from meta before cloning
  const { meta, ...rest } = value;
  const serializableMeta = removeNonSerializableProperties(meta);

  const clonedValue = structuredClone({
    ...rest,
    meta: serializableMeta,
  });

  if (isSerializableBlockExport(clonedValue)) {
    return clonedValue;
  }

  throw new Error("Cloned value is not a SerializableBlockExport");
};

export interface DragAndDropProviderProps {
  children: ReactNode;
}

export const DragAndDropProvider = memo(
  ({ children }: DragAndDropProviderProps) => {
    const { error } = useToast();

    // Combined subscription for all drag and drop functions
    const {
      setDraggingTemplate,
      addBlocks,
      closeSidebarView,
      setIsDragging,
      setSelectedBlockPath,
      dragAndDropType,
    } = useBuilderStore(state => ({
      setDraggingTemplate: state.setDraggingTemplate,
      addBlocks: state.addBlocks,
      closeSidebarView: state.closeSidebarView,
      setIsDragging: state.setIsDragging,
      setSelectedBlockPath: state.setSelectedBlockPath,
      dragAndDropType: state.dragAndDropType,
    }));
    const dragDataRef = useRef<SerializableBlockExport | null>(null);

    const sensorInstances = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: { distance: 5 },
      }),
    );

    const handleCollisionDetection = useCallback(
      (args: CollisionArgs) => conditionalClosestCenter(args),
      [],
    );

    const handleDragStart = useCallback(
      (event: DragStartEvent) => {
        if (dragAndDropType === "disabled") return;

        setIsDragging(true);

        const draggableData = event.active.data.current;
        if (!isBlockExport(draggableData)) return;

        setSelectedBlockPath(null);

        const clonedDraggableData = isTemplateExport(draggableData)
          ? cloneAsSerializableBlockExport(draggableData)
          : cloneAsSerializableBlockExport({
              defaultData: draggableData.defaultData,
              imagePreview: draggableData.imagePreview,
              type: draggableData.type,
              meta: draggableData.meta,
            });

        dragDataRef.current = clonedDraggableData;
        setDraggingTemplate(clonedDraggableData);
      },
      [
        dragAndDropType,
        setIsDragging,
        setDraggingTemplate,
        setSelectedBlockPath,
      ],
    );

    // TODO: Handle sorting of blocks inside the canvas. Right now it assumes all drag actions are adding blocks.
    const handleDragEnd = useCallback(
      (event: DragEndEvent) => {
        if (dragAndDropType === "disabled") return;

        setIsDragging(false);
        setDraggingTemplate(null);

        const droppable = event.over;
        const draggableData = dragDataRef.current;
        dragDataRef.current = null;

        if (!draggableData || !droppable) return;

        const droppableData = droppable.data.current;

        const isValidDrop =
          isDroppableData(droppableData) &&
          isSerializableBlockExport(draggableData);

        if (!isValidDrop) return;

        const isTemplate = isTemplateExport(draggableData);
        const { accepts, rejects, index, path } = droppableData;
        const categories = draggableData.meta.category ?? [];

        const canBeAdded = isAcceptableToDrop({ accepts, categories, rejects });

        if (!canBeAdded) {
          error(t("builder_ui.errors.invalid_element_drop"));
          return;
        }

        if (isTemplate) {
          addBlocks(draggableData.templateData, path, index);
        } else {
          addBlocks(draggableData.defaultData, path, index);
        }

        closeSidebarView();
      },
      [
        dragAndDropType,
        setIsDragging,
        setDraggingTemplate,
        addBlocks,
        error,
        closeSidebarView,
      ],
    );

    return (
      <DndContext
        collisionDetection={handleCollisionDetection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        sensors={sensorInstances}
      >
        {children}
      </DndContext>
    );
  },
);

DragAndDropProvider.displayName = "DragAndDropProvider";
